import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Select,
  Switch,
  DatePicker,
  Spin,
} from "antd";
import dayjs from "dayjs";
import { subItemHooks } from "../hooks";

const { Option } = Select;

const SendSolapiModal = ({
  visible,
  onClose,
  onCreate, // 사용되지 않지만 유지
  selectedIds = [],
}) => {
  const [form] = Form.useForm();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [templateMessage, setTemplateMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isReserved, setIsReserved] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchSolapi = async (currentPage = 1) => {
    if (!hasMore || loading) return;
    setLoading(true);
    try {
      const response = await subItemHooks.solapiList({ page: currentPage });
      const { data, total_count } = response.data;

      const formatted = data.map((tpl) => {
        let parsedVars = [];
        try {
          parsedVars = Object.keys(JSON.parse(tpl.variables || "{}"));
        } catch {
          parsedVars = [];
        }
        return {
          id: tpl.template_id,
          name: tpl.title || tpl.message,
          message: tpl.message || "",
          variables: parsedVars,
        };
      });

      setTemplateList((prev) => {
        const seen = new Set(prev.map((tpl) => tpl.id));
        const newTemplates = formatted.filter((tpl) => !seen.has(tpl.id));
        return [...prev, ...newTemplates];
      });

      if ((page - 1) * 10 + formatted.length >= total_count) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("템플릿 데이터를 가져오는 중 오류 발생:", error);
      message.error("템플릿을 불러오는 데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setTemplateList([]);
      setPage(1);
      setHasMore(true);
      setSelectedTemplate(null);
      setVariables([]);
      setTemplateMessage("");
      fetchSolapi(1);
    }
  }, [visible]);

  useEffect(() => {
    if (selectedTemplate) {
      const found = templateList.find((tpl) => tpl.id === selectedTemplate);
      setVariables(found?.variables || []);
      setTemplateMessage(found?.message || "");
      form.setFieldsValue({ template: selectedTemplate });
    }
  }, [selectedTemplate]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchSolapi(nextPage);
    }
  };

  const handleSend = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedIds.length) {
        message.warning("전송할 유저를 선택해주세요.");
        return;
      }

      const words = {};
      variables.forEach((key) => {
        words[key] = values[`variables_${key}`] || "";
      });

      const payload = {
        id: selectedIds,
        template_id: selectedTemplate,
        words,
        reservation: isReserved ? 1 : 0,
        reservation_date: isReserved
          ? dayjs(values.reservation_date).format("YYYY-MM-DD HH:mm:ss")
          : null,
      };

      const res = await subItemHooks.solapiSend(payload);

      if (res.IsError) {
        message.error("메시지 전송 실패");
        return;
      }

      message.success("메시지 전송 완료");
      form.resetFields();
      setSelectedTemplate(null);
      setIsReserved(false);
      setTemplateMessage("");
      onClose();
    } catch (err) {
      console.error(err);
      message.error("전송 실패: 입력값을 확인해주세요.");
    }
  };

  return (
    <Modal
      title="솔리피 메시지 전송"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Spin spinning={loading && page === 1}>
        <Form form={form} layout="vertical">
          <Form.Item
            label="템플릿 선택"
            name="template"
            rules={[{ required: true, message: "템플릿을 선택해주세요." }]}
          >
            <Select
              placeholder="템플릿을 선택하세요"
              onChange={(value) => {
                setSelectedTemplate(value);
                form.setFieldsValue({ template: value });
              }}
              onPopupScroll={handleScroll}
              loading={loading}
              notFoundContent={loading ? "로딩 중..." : "결과 없음"}
            >
              {templateList.map((tpl) => (
                <Option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {templateMessage && (
            <Form.Item label="템플릿 메시지">
              <Input.TextArea value={templateMessage} readOnly autoSize />
            </Form.Item>
          )}

          {variables.map((key) => (
            <Form.Item
              key={key}
              name={`variables_${key}`}
              label={`${key} 값 입력`}
              rules={[{ required: true, message: `${key} 값을 입력해주세요.` }]}
            >
              <Input />
            </Form.Item>
          ))}

          <Form.Item label="예약 전송 여부">
            <Switch checked={isReserved} onChange={setIsReserved} />
          </Form.Item>

          {isReserved && (
            <Form.Item
              name="reservation_date"
              label="예약 발송 시간"
              rules={[{ required: true, message: "예약 발송 시간을 선택해주세요." }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                style={{ width: "100%" }}
                disabledDate={(current) => current && current < dayjs().startOf("day")}
              />
            </Form.Item>
          )}

          <div style={{ marginTop: 20, textAlign: "right" }}>
            <Button
              type="primary"
              onClick={handleSend}
              style={{ marginRight: 8 }}
              disabled={loading}
            >
              메시지 전송
            </Button>
            <Button onClick={onClose}>취소</Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default SendSolapiModal;
